import { LocationInput } from "@/types/custom";
import { useToast } from "@/hooks/use-toast";
import { ItineraryItem } from "@/types/trip";
import { calculateStartTime } from "@/lib/experiences/newExperience";
import { experienceService } from "@/services/experienceService";
import useTripStore from "@/stores/tripBuilder/useTrip";
import useCustomActivityStore from "@/stores/tripBuilder/useCustomActivity";

export const useHandleNext = () => {
  const { toast } = useToast();

  // Validation functions for each step
  const validateTitle = (title: string) => {
    if (!title.trim()) {
      toast({
        title: "Name required",
        description: "Please enter a name for your activity",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const validateLocation = (location: LocationInput) => {
    if (!location.value.trim() || !location.coordinates) {
      toast({
        title: "Location required",
        description: "Please select a location for your activity",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const validateImages = (images: any[]) => {
    if (images.length === 0) {
      toast({
        title: "Image required",
        description: "Please add at least one image for your activity",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  // Final submission function
  const submitActivity = async () => {
    const {
      title,
      description,
      duration,
      cost,
      categories,
      location,
      images,
      selectedOption,
    } = useCustomActivityStore.getState();

    const defaultCoordinates = { latitude: 0, longitude: 0 };
    const coordinates = location.coordinates
      ? {
          latitude: location.coordinates.latitude,
          longitude: location.coordinates.longitude,
        }
      : defaultCoordinates;

    const customId = crypto.randomUUID();

    const itineraryItem: ItineraryItem = {
      name: title,
      start_time: null,
      duration: parseFloat(duration) || null,
      images: images.map((img) => img.url).filter(Boolean) as string[],
      coordinates,
      sourceId: {
        waytoraPlaceId: customId,
      },
      metadata: {
        description: description,
      },
      pricing: parseFloat(cost) || 0,
      id: customId + Date.now().toString(),
      type: "community",
      bookingStatus: "pending",
      categories: categories,
      participants: null,
    };

    try {
      // Get the selected location and day
      const tripData = useTripStore.getState().tripData;
      const selectedDay = useTripStore.getState().selectedDay;
      const selectedLocation = useTripStore.getState().selectedLocation;

      if (!tripData || !selectedDay || !selectedLocation) {
        toast({
          title: "Error",
          description: "Please select a location and day first",
          variant: "destructive",
        });
        return false;
      }

      const currentLocation = tripData.locations.find(
        (location) => location.id === selectedLocation.id
      );

      if (!currentLocation || !currentLocation.id) {
        toast({
          title: "Error",
          description: "Invalid location selected",
          variant: "destructive",
        });
        return false;
      }

      // Find the current day's itinerary items
      const currentDayItems = currentLocation?.itinerary_items.find(
        (item) => item.date === selectedDay
      );

      if (!currentDayItems) {
        toast({
          title: "Error",
          description: "Could not find the selected day in the itinerary",
          variant: "destructive",
        });
        return false;
      }

      // Calculate the start time for the new activity
      const startTime = await calculateStartTime(
        {
          date: selectedDay,
          items: currentDayItems.items,
        },
        selectedLocation.date.time_zone
      );

      // Update the itinerary item with the calculated start time
      itineraryItem.start_time = startTime;

      // Add the item to the trip's itinerary
      const updatedItems = [...currentDayItems.items, itineraryItem];
      const updatedItineraryItems = currentLocation?.itinerary_items.map(
        (item) =>
          item.date === selectedDay ? { ...item, items: updatedItems } : item
      );

      // Update the location
      const updatedLocation = {
        ...currentLocation,
        itinerary_items: updatedItineraryItems,
      };

      // Update the trip data
      const updatedTripData = {
        ...tripData,
        locations: tripData.locations.map((location) =>
          location.id === selectedLocation.id ? updatedLocation : location
        ),
      };

      // Update the trip data
      useTripStore.getState().updateTripData(updatedTripData);

      // Create location update
      await useTripStore
        .getState()
        .createLocationUpdate(
          selectedLocation.waytora_id,
          selectedLocation.id,
          "experience_added",
          {
            itemName: itineraryItem.name,
            itemType: "experience",
            duration: itineraryItem.duration,
            images: images.map((img) => img.url).filter(Boolean) as string[],
          }
        );

      // Create the activity in the backend
      await experienceService.createCustomActivity(
        itineraryItem,
        {
          waytora_id: selectedLocation.waytora_id,
          city: selectedLocation.name,
          country: selectedLocation.address_components.country,
        },
        selectedOption === "library"
      );

      return true;
    } catch (error) {
      console.error("Error creating activity:", error);
      toast({
        title: "Error",
        description: "Failed to create activity. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  };

  // Main handle next function
  const handleNext = async (
    currentStep: number,
    setCurrentStep: (step: number) => void,
    totalSteps: number,
    selectedLocationData?: LocationInput
  ) => {
    let locationToCheck: LocationInput;

    // Step-specific validation
    switch (currentStep) {
      case 0: // Title step
        if (!validateTitle(useCustomActivityStore.getState().title)) return;
        break;
      case 5: // Location step
        locationToCheck =
          selectedLocationData || useCustomActivityStore.getState().location;
        if (!validateLocation(locationToCheck)) return;
        break;
      case 6: // Images step
        if (!validateImages(useCustomActivityStore.getState().images)) return;
        break;
    }

    // Move to next step or submit
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      await submitActivity();
      useCustomActivityStore.getState().reset();
    }
  };

  return { handleNext };
};
